import { useEffect, useMemo, useRef, useState } from "react";
import { DropFiles } from "./DropFiles";
import type { Pin } from "@centerline/core/viewer";
import { api } from "./api";
import { isNetworkError, newUuid, queuePin, queuedPins, type QueuedReport } from "./offline";
import { compressPhoto } from "./photo";
import type { Me } from "./session";

export interface PendingPhotoPin { id: string; pageId: string; x: number; y: number; title: string; urls: string[]; createdById?: string; type: Pin["type"] }

/** Photo pins made offline that haven't synced yet, for one plan set. Reloads after each sync. */
export function usePendingPhotoPins(documentId: string) {
  const [items, setItems] = useState<PendingPhotoPin[]>([]);
  useEffect(() => {
    let urls: string[] = [];
    const load = () => queuedPins(documentId).then((qs: QueuedReport[]) => {
      urls.forEach(URL.revokeObjectURL);
      const next = qs.map((q) => ({ id: q.id, pageId: q.pin!.pageId, x: q.pin!.x, y: q.pin!.y, title: q.pin!.title, type: q.pin!.type ?? "Photo", createdById: q.pin!.createdById, urls: q.photos.map((p) => URL.createObjectURL(p.blob)) }));
      urls = next.flatMap((n) => n.urls);
      setItems(next);
    });
    void load();
    const on = () => void load();
    window.addEventListener("cl:synced", on); window.addEventListener("cl:queued", on);
    return () => { window.removeEventListener("cl:synced", on); window.removeEventListener("cl:queued", on); urls.forEach(URL.revokeObjectURL); };
  }, [documentId]);
  /** As Pin objects so the normal pin layer can draw them. */
  const asPins = useMemo(() => items.map((p): Pin => ({ id: p.id, projectId: "", documentId, pageId: p.pageId, x: p.x, y: p.y, type: p.type, title: p.title, status: "open", description: "", attachments: [], createdBy: "", createdAt: "", updatedAt: "", version: 0 })), [items, documentId]);
  return { items, asPins };
}

/** Bottom sheet after tapping a spot with the Photo pin tool: camera or library, optional caption. */
export function PhotoPinSheet({ projectId, documentId, pageId, x, y, me, punch, onCancel, onSaved }: {
  projectId: string; documentId: string; pageId: string; x: number; y: number; me: Me | null;
  /** Punch item mode: what's wrong, assignee and due date; photos are optional. */
  punch?: boolean;
  onCancel: () => void; onSaved: (r: { pin?: Pin; queued?: boolean }) => void;
}) {
  const [files, setFiles] = useState<File[]>([]);
  const [caption, setCaption] = useState("");
  const [desc, setDesc] = useState("");
  const [assignee, setAssignee] = useState("");
  const [due, setDue] = useState("");
  const ready = punch ? !!caption.trim() : !!files.length;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const openedAt = useRef(Date.now()); // the tap that placed the pin also fires a click on this backdrop
  const previews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => previews.forEach(URL.revokeObjectURL), [previews]);
  const add = async (list: FileList | File[] | null) => {
    if (!list?.length) return;
    setBusy(true);
    try { const out = await Promise.all([...list].map((f) => compressPhoto(f))); setFiles((xs) => [...xs, ...out]); }
    finally { setBusy(false); }
  };
  const save = async () => {
    if (!ready) return;
    setBusy(true); setErr("");
    const id = newUuid();
    const photos = files.map((f) => ({ id: newUuid(), caption, name: f.name, type: f.type, blob: f as Blob }));
    const fields = { documentId, pageId, x, y, title: caption.trim() || "Photo", description: punch ? desc.trim() : "", ...(punch ? { type: "Punch" as const, assignee: assignee.trim(), dueDate: due } : {}), createdBy: me?.displayName ?? "", ...(me ? { createdById: me.id } : {}) };
    const queue = async () => { await queuePin(id, projectId, fields, photos); window.dispatchEvent(new Event("cl:queued")); onSaved({ queued: true }); };
    try {
      if (!navigator.onLine) { await queue(); return; }
      const made = (await api.createPin(projectId, { type: "Photo", ...fields, id } as Partial<Pin>)).pin;
      if (!photos.length) { onSaved({ pin: made }); return; }
      const form = new FormData();
      for (const p of photos) { form.append("attachmentId", p.id); form.append("file", p.blob, p.name); }
      const res = await fetch(`/api/pins/${id}/attachments`, { method: "POST", body: form, credentials: "same-origin", headers: (await import("./api")).identity() });
      if (!res.ok) throw Object.assign(new Error((await res.json().catch(() => ({}))).message ?? `Upload failed (${res.status})`), { status: res.status });
      onSaved({ pin: (await res.json()).pin });
    } catch (e) {
      // Lost signal part way: the outbox retries with the same ids, so nothing is doubled.
      if (isNetworkError(e) || !(e as { status?: number }).status || (e as { status: number }).status >= 502) { await queue(); return; }
      setErr((e as Error).message);
    } finally { setBusy(false); }
  };
  return (
    <div className="photo-sheet-backdrop" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget && !busy && Date.now() - openedAt.current > 500) onCancel(); }}>
      <div className="photo-sheet" role="dialog" aria-label={punch ? "Punch item" : "Photo pin"} data-testid={punch ? "punch-sheet" : "photo-pin-sheet"}>
        <h3>{punch ? "✅ Punch item" : "📷 Photo pin"}</h3>
        {punch && <>
          <label>What needs fixing<input value={caption} maxLength={200} autoFocus placeholder="e.g. Grout gap at MH-3" data-testid="punch-title" onChange={(e) => setCaption(e.target.value)} /></label>
          <label>Details (optional)<textarea value={desc} rows={2} maxLength={2000} data-testid="punch-desc" onChange={(e) => setDesc(e.target.value)} /></label>
          <div className="row">
            <label style={{ flex: 2 }}>Assigned to<input value={assignee} maxLength={120} placeholder="Person or sub" data-testid="punch-assignee" onChange={(e) => setAssignee(e.target.value)} /></label>
            <label style={{ flex: 1 }}>Due (optional)<input type="date" value={due} data-testid="punch-due" onChange={(e) => setDue(e.target.value)} /></label>
          </div>
        </>}
        <DropFiles testId="drop-photopin" accept="image/*" kind="a photo" disabled={busy} label="Drop photos here" onFiles={(fs) => void add(fs)}>
        <div className="photo-sheet-previews">
          {previews.map((u, i) => <img key={u} src={u} alt={`Photo ${i + 1}`} />)}
          {!previews.length && <p className="muted">{punch ? "Photos are optional." : "Take a photo or pick one from the library."}</p>}
        </div>
        <div className="row">
          <label className="button-like primary big">📷 Take photo<input type="file" accept="image/*" capture="environment" hidden data-testid="photo-camera" onChange={(e) => { void add(e.target.files); e.target.value = ""; }} /></label>
          <label className="button-like big">🖼 Library<input type="file" accept="image/*" multiple hidden data-testid="photo-library" onChange={(e) => { void add(e.target.files); e.target.value = ""; }} /></label>
        </div>
        </DropFiles>
        {!punch && <label>Caption (optional)<input value={caption} maxLength={200} placeholder="e.g. Inlet S-5 poured" onChange={(e) => setCaption(e.target.value)} /></label>}
        {err && <p className="error">{err}</p>}
        <div className="row end">
          <button type="button" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="primary" disabled={busy || !ready} onClick={save} data-testid={punch ? "punch-save" : "photo-save"}>{busy ? "Saving…" : `Save${navigator.onLine ? "" : " (syncs later)"}`}</button>
        </div>
      </div>
    </div>
  );
}

/** Full-screen photo for a Photo pin (or one still waiting to sync). */
export function PhotoView({ title, sheet, by, srcs, pending, canEdit, onDetails, onDelete, onClose }: {
  title: string; sheet?: string; by?: string; srcs: string[]; pending?: boolean; canEdit: boolean;
  onDetails?: () => void; onDelete?: () => void; onClose: () => void;
}) {
  const [i, setI] = useState(0);
  useEffect(() => { const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowRight") setI((n) => Math.min(srcs.length - 1, n + 1)); if (e.key === "ArrowLeft") setI((n) => Math.max(0, n - 1)); }; window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [srcs.length, onClose]);
  return (
    <div className="photo-full" role="dialog" aria-label="Photo" data-testid="photo-full" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
      <header>
        <div><b>{title === "Photo" ? "Photo" : title}</b><small>{[sheet && `Sheet ${sheet}`, by, pending && "waiting to sync"].filter(Boolean).join(" · ")}</small></div>
        <div className="row">
          {onDetails && <button type="button" onClick={onDetails}>Details</button>}
          {canEdit && onDelete && <button type="button" className="danger" onClick={() => { if (confirm("Delete this photo pin? (The photo stays in the daily report.)")) onDelete(); }}>Delete</button>}
          <button type="button" className="ghost" aria-label="Close" onClick={onClose}>✕</button>
        </div>
      </header>
      <div className="photo-full-img">{srcs[i] ? <img src={srcs[i]} alt={title} /> : <p>No photo attached yet.</p>}</div>
      {srcs.length > 1 && <footer>{srcs.map((_, n) => <button key={n} type="button" className={`chip small ${n === i ? "on" : ""}`} onClick={() => setI(n)}>{n + 1}</button>)}</footer>}
    </div>
  );
}
